import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "react-modal";
import SEO from "../components/SEO";
import { firestore } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 14px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
  letter-spacing: 0.3px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.6;
  max-width: 820px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const BtnGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 900;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.35);
    outline-offset: 3px;
  }
`;

/* ---------- Filters ---------- */

const Toolbar = styled.div`
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "rgba(15,118,110,0.10)" : "#fff")};
  color: ${({ $active }) => ($active ? "#0f766e" : "#111827")};
  font-weight: 950;
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: ${({ $active }) => ($active ? "rgba(15,118,110,0.14)" : "#f9fafb")};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const RightTools = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const Count = styled.div`
  color: #6b7280;
  font-weight: 900;
  font-size: 13px;
`;

const JumpSelect = styled.select`
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 10px 12px;
  font-weight: 900;
  color: #111827;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

/* ---------- Grid / Cards ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const Card = styled.article`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  overflow: hidden;

  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.1);
  }
`;

const ThumbButton = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  width: 100%;
  cursor: zoom-in;
  background: #000;
  touch-action: manipulation;
`;


const Thumb = styled.img`
  width: 100%;
  height: 190px;
  object-fit: cover;
  display: block;

  @media (min-width: 900px) {
    height: 210px;
  }
`;

const CardBody = styled.div`
  padding: 14px 14px 16px;
`;

const CourseTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  color: #111827;
`;

const Meta = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 800;
`;

const ButtonRow = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #0f766e;
  color: #fff;
  font-weight: 900;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.03);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const BtnSecondary = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
    filter: none;
  }
`;

const Skeleton = styled.div`
  height: 220px;
  border-radius: 18px;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% {
      background-position: 0% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const Empty = styled.div`
  padding: 10px 0;
  color: #6b7280;
  font-weight: 900;
`;

/* ---------- Modal ---------- */

Modal.setAppElement("#root");

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(0,0,0,0.78)",
    zIndex: 9999, // ✅ över header
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px",
  },
  content: {
    position: "relative",
    inset: "auto",
    padding: 0,
    borderRadius: "18px",
    border: "1px solid rgba(255,255,255,0.18)",
    overflow: "hidden",
    boxShadow: "0 18px 70px rgba(0,0,0,0.55)",
    width: "min(1000px, 94vw)",
    maxHeight: "92vh",
  },
};


const ModalTop = styled.div`
  background: #0b1220;
  color: #fff;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ModalTitle = styled.div`
  font-weight: 900;
  font-size: 14px;
  opacity: 0.95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 900;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.55);
    outline-offset: 3px;
  }
`;

const ModalBody = styled.div`
  background: #000;
  display: grid;
  place-items: center;
  padding: 8px;
  overflow: auto;
`;

const ModalImg = styled.img`
  max-width: 100%;
  max-height: calc(92vh - 56px);
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
`;


/* ---------- helpers ---------- */

const normalizeType = (c) => {
  const raw = (c.type || c.category || c.courseType || "").toString().trim();
  const name = (c.name || "").toString().toLowerCase();
  const merged = `${raw} ${name}`.toLowerCase();

  if (merged.includes("äventyr") || merged.includes("aventyr")) return "Äventyrsgolf";
  if (merged.includes("bangolf")) return "Bangolf";

  // fallback
  return "Bangolf";
};

/* ---------- Component ---------- */

const Court = () => {
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);

  // ALL | BANGOLF | AVENTYR
  const [filter, setFilter] = useState("ALL");

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(firestore, "golf_courses"));
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // ✅ sort 1..18
        data.sort((a, b) => {
          const ah = Number.isFinite(Number(a.holes)) ? Number(a.holes) : 9999;
          const bh = Number.isFinite(Number(b.holes)) ? Number(b.holes) : 9999;
          return ah - bh;
        });

        setCourts(data);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const yearLabel = useMemo(() => new Date().getFullYear(), []);

  const openModal = (src, title) => {
    if (!src) return;
    setModalSrc(src);
    setModalTitle(title || "Bild");
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setModalSrc("");
    setModalTitle("");
  };

  const filtered = useMemo(() => {
    const mapped = (courts || []).map((c) => ({
      ...c,
      _typeLabel: normalizeType(c),
      _holesNum: Number.isFinite(Number(c.holes)) ? Number(c.holes) : undefined,
    }));

    return mapped.filter((c) => {
      if (filter === "ALL") return true;
      if (filter === "BANGOLF") return c._typeLabel === "Bangolf";
      if (filter === "AVENTYR") return c._typeLabel === "Äventyrsgolf";
      return true;
    });
  }, [courts, filter]);

  const holesList = useMemo(() => {
    // dropdown 1..18 som finns
    return filtered
      .map((c) => c._holesNum)
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
  }, [filtered]);

  const jumpToHole = (hole) => {
    if (!hole) return;
    const el = document.getElementById(`hole-${hole}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <SEO title="Banor" description="Upptäck våra olika banor." keywords="banor, bangolf, äventyrsgolf, hofors bgk" />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>Våra banor {yearLabel}</Title>
              <Subtitle>
                Filtrera Bangolf / Äventyrsgolf och bläddra banor i ordning (1–18). Klicka på en bild för att se den större.
              </Subtitle>
            </div>

            <Actions>
              <BtnGhost to="/">Tillbaka</BtnGhost>
            </Actions>
          </Hero>

          <Toolbar>
            <FilterRow>
              <FilterBtn $active={filter === "ALL"} onClick={() => setFilter("ALL")}>
                Alla
              </FilterBtn>
              <FilterBtn $active={filter === "BANGOLF"} onClick={() => setFilter("BANGOLF")}>
                Bangolf
              </FilterBtn>
              <FilterBtn $active={filter === "AVENTYR"} onClick={() => setFilter("AVENTYR")}>
                Äventyrsgolf
              </FilterBtn>
            </FilterRow>

            <RightTools>
              {holesList.length > 0 && (
                <JumpSelect
                  defaultValue=""
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v)) jumpToHole(v);
                  }}
                >
                  <option value="">Hoppa till bana…</option>
                  {holesList.map((h) => (
                    <option key={h} value={h}>
                      Bana {h}
                    </option>
                  ))}
                </JumpSelect>
              )}
              <Count>Visar {filtered.length} banor</Count>
            </RightTools>
          </Toolbar>

          {loading ? (
            <Grid>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} />
              ))}
            </Grid>
          ) : !filtered.length ? (
            <Empty>Inga banor matchar filtret.</Empty>
          ) : (
            <Grid>
              {filtered.map((c) => {
                const holes = Number.isFinite(Number(c.holes)) ? Number(c.holes) : null;
                const name = c.name || "Bana";
                const typeLabel = c._typeLabel || "Bana";

                return (
                  <Card key={c.id} id={holes ? `hole-${holes}` : undefined}>
                    <ThumbButton
                      type="button"
                      onClick={() => openModal(c.imageUrl, `${holes ? `Bana ${holes}` : ""} ${name}`)}
                      aria-label={`Öppna bild för ${name}`}
                    >
                      <Thumb src={c.imageUrl} alt={name} />
                    </ThumbButton>

                    <CardBody>
                      <CourseTitle>
                        {holes ? `Bana ${holes} • ` : ""}
                        {name}
                      </CourseTitle>

                      <Meta>{typeLabel} • Tryck på knapparna för bild eller tips.</Meta>

                      <ButtonRow>
                        <Btn type="button" onClick={() => openModal(c.imageUrl, `${name} – Bild`)}>
                          Visa bild
                        </Btn>

                        <BtnSecondary
                          type="button"
                          onClick={() => openModal(c.tipImageUrl, `${name} – Tips`)}
                          disabled={!c.tipImageUrl}
                          aria-disabled={!c.tipImageUrl}
                          style={!c.tipImageUrl ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                        >
                          Tips för banan
                        </BtnSecondary>
                      </ButtonRow>
                    </CardBody>
                  </Card>
                );
              })}
            </Grid>
          )}
        </Container>
      </Page>

      <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyles} contentLabel="Bana bild">
        <ModalTop>
          <ModalTitle>{modalTitle}</ModalTitle>
          <CloseBtn type="button" onClick={closeModal}>
            Stäng ✕
          </CloseBtn>
        </ModalTop>

        <ModalBody>{modalSrc && <ModalImg src={modalSrc} alt={modalTitle} />}</ModalBody>
      </Modal>
    </>
  );
};

export default Court;


